// API Types
import type { EffectiveAccess } from './accessControl';

export interface User {
  username: string;
  email: string;
  name: string;
  roles: string[];
  groups: string[];
  is_authenticated: boolean;
  /** App-managed ACL resolved by /api/auth/me */
  effective_access?: EffectiveAccess;
}

export const ROLE_HIERARCHY = {
  administrator: 5,
  user_access_administrator: 4,
  analyst: 3,
  viewer: 2,
  guest: 1
} as const;

export type UserRole = keyof typeof ROLE_HIERARCHY;

// Azure AD Authentication
export interface AzureAuthResponse {
  access_token: string;
  token_type: string;
  expires_in?: number;
  refresh_token?: string;
  user?: User;
}

export interface AuthUrlResponse {
  auth_url: string;
}

// Permission check utilities
export function checkPermission(userRoles: string[], requiredRole: UserRole): boolean {
  const userLevel = Math.max(...userRoles.map(role => ROLE_HIERARCHY[role as UserRole] || 0));
  const requiredLevel = ROLE_HIERARCHY[requiredRole];
  return userLevel >= requiredLevel;
}

/** COP dashboard: analyst (or higher via hierarchy) or dedicated PF external guests group */
export function canAccessCop(userRoles: string[]): boolean {
  if (userRoles.includes('pf_external_guest')) return true;
  return checkPermission(userRoles, 'analyst');
}

export function getUserHighestRole(userRoles: string[]): UserRole | null {
  const roleHierarchy = Object.entries(ROLE_HIERARCHY);
  const userLevels = userRoles.map(role => ROLE_HIERARCHY[role as UserRole] || 0);
  const highestLevel = Math.max(...userLevels);
  
  const highestRole = roleHierarchy.find(([, level]) => level === highestLevel);
  return highestRole ? (highestRole[0] as UserRole) : null;
}

// Dataset Types
export enum DeviceType {
  VEHICLE = 'Vehicle',
  PHONE = 'Phone',
  WATCH = 'Watch',
}

export enum DatasetStatus {
  UPLOADED = 'uploaded',
  PROCESSING = 'processing',
  PROCESSED = 'processed',
  ERROR = 'error',
}

export interface Dataset {
  id: number;
  name: string;
  description?: string;
  device_type: DeviceType;
  status: DatasetStatus;
  original_filename?: string;
  file_size?: number;
  total_points?: number;
  date_range_start?: string;
  date_range_end?: string;
  students?: string[];
  uploaded_at: string;
  processed_at?: string;
  error_message?: string;
}

export interface UploadResponse {
  dataset_id: number;
  message: string;
  status: string;
}

// Analysis Types
export enum AnalysisType {
  QUICK = 'quick',
  FULL = 'full',
  ROUTE_ONLY = 'route_only',
  TEMPORAL_ONLY = 'temporal_only',
  MULTI_DEVICE = 'multi_device',
  POL_REPORT = 'pol_report',
}

/**
 * Pattern of Life analysis categories selectable for a POL Report job.
 * Mirrors PolAnalysisTypeEnum in backend/app/schemas/analysis.py.
 */
export type PolAnalysisType =
  | 'repeated_routes'
  | 'dwells'
  | 'anomalies'
  | 'patterns'
  | 'correlations'
  | 'sequences'
  | 'digital'
  | 'atypicality';

export interface PolAnalysisTypeInfo {
  id: PolAnalysisType;
  label: string;
  description: string;
  /** Longer tooltip copy explaining what the analyst gets. */
  detail: string;
}

export const POL_ANALYSIS_TYPE_INFO: PolAnalysisTypeInfo[] = [
  {
    id: 'repeated_routes',
    label: 'Repeated Routes',
    description: 'Recurring origin-to-destination travel patterns',
    detail:
      'Route clusters with GPS geometry, usage counts, consistency scores, peak travel times, and individual trip instances. Includes routes shared with other entities.',
  },
  {
    id: 'dwells',
    label: 'Dwells & Top Locations',
    description: 'Significant stop locations and visit statistics',
    detail:
      'Dwell locations with visit counts, total and average durations, and each entity\'s most-frequented places over the reporting window.',
  },
  {
    id: 'anomalies',
    label: 'Anomalies & Detector Events',
    description: 'ML day flags and statistical detector events',
    detail:
      'Machine-learning flagged days (physical and digital) plus statistical detections: routine breaks, unusual locations, time shifts, dwell duration anomalies, lapse breaks, route deviations, and new relationships.',
  },
  {
    id: 'patterns',
    label: 'Recurring Patterns',
    description: 'Repeated location, route, and communication habits',
    detail:
      'Detected recurring behaviors (e.g. "visits Location X every Tuesday 14:00-16:00") with frequency, adherence rate, confidence, and active/emerging/breaking status.',
  },
  {
    id: 'correlations',
    label: 'Entity Correlations',
    description: 'Relationships between entities',
    detail:
      'Co-location, co-travel, leader-follower, sequential, and communication links between entities, with strength and confidence scores and shared locations/routes.',
  },
  {
    id: 'sequences',
    label: 'Location Sequences',
    description: 'Ordered multi-stop movement chains',
    detail:
      'Repeated A-to-B-to-C location chains with occurrence counts, typical start times, and average transition durations.',
  },
  {
    id: 'digital',
    label: 'Digital Patterns',
    description: 'App usage, browsing, and communication behavior',
    detail:
      'Digital behavior patterns such as app usage habits, browsing schedules, and communication style, each with confidence and occurrence statistics.',
  },
  {
    id: 'atypicality',
    label: 'Daily Atypicality',
    description: 'Per-day behavioral deviation scores',
    detail:
      'Day-by-day atypicality scoring per entity combining mobility metrics and anomaly counts, surfacing the most unusual days in the window.',
  },
];

export const ALL_POL_ANALYSIS_TYPES: PolAnalysisType[] = POL_ANALYSIS_TYPE_INFO.map(
  (t) => t.id
);

export enum JobStatus {
  PENDING = 'pending',
  RUNNING = 'running',
  COMPLETED = 'completed',
  FAILED = 'failed',
  CANCELLED = 'cancelled',
}

export interface AnalysisParameters {
  enable_dwell_detection: boolean;
  enable_dwell_based_routes: boolean;
  enable_non_dwell_routes: boolean;
  enable_temporal_analysis: boolean;
  enable_connectivity_analysis: boolean;
  enable_route_consistency: boolean;
  enable_place_lookup: boolean;
  enable_ai_insights: boolean;
  dwell_radius: number;
  stay_point_time_threshold: number;
  stay_point_distance_threshold: number;
  min_route_length: number;
  min_segment_distance: number;
  route_similarity_threshold: number;
  max_dwell_locations: number;
  max_routes: number;
}

export interface AnalysisRequest {
  name?: string;
  analysis_type: AnalysisType;
  device_type: DeviceType | null; // null for multi-device analysis (legacy)
  device_types?: DeviceType[]; // Array of selected device types for multi-selection
  dataset_id?: number; // Optional when using SENTINEL Portal
  course_id?: string; // SENTINEL course ID
  students?: string[];
  date_range_start?: string;
  date_range_end?: string;
  parameters?: AnalysisParameters;
  /** POL report only: selected Pattern of Life categories ('all' or subset). */
  pol_analysis_types?: (PolAnalysisType | 'all')[];
}

export interface AnalysisJob {
  id: number;
  name?: string;
  analysis_type?: string;
  status: JobStatus;
  progress_percentage: number;
  current_step?: string;
  created_at?: string;
  estimated_completion?: string;
  start_time: string;
  end_time?: string;
  error_message?: string;
  error_traceback?: string;
  dataset_id?: number;
  course_id?: string;
  students: string[];
  date_range_start: string;
  date_range_end: string;
  parameters?: AnalysisParameters;
  results_summary?: Record<string, any>;
  processing_duration?: number;
  dwell_locations?: DwellLocation[];
  routes?: Route[];
  output_files?: string[];
  user_id?: string;  // User who created the job
}

export interface AnalysisProgress {
  job_id: number;
  status: JobStatus;
  progress_percentage: number;
  current_step: string;
  message?: string;
  estimated_completion?: string;
}

// Results Types
// Enhanced visit record structure for temporal analysis
export interface VisitRecord {
  start_time: string;
  end_time?: string | null;
  duration_seconds?: number;
  entity_id: string;
}

export interface DwellLocation {
  id: number;
  center_latitude: number;
  center_longitude: number;
  radius?: number;
  total_visits?: number;
  total_duration?: number;
  average_duration?: number;
  max_duration?: number;
  place_name?: string;
  place_type?: string;
  address?: string;
  confidence_score?: number;  // Geocoding confidence (0.0-1.0)
  metadata?: any; // Geocoding metadata (name, address, etc.)
  students?: string[];
  visit_times?: string[] | VisitRecord[]; // Support both old (string[]) and new (VisitRecord[]) formats
  time_of_day_pattern?: { [hour: string]: number }; // Hourly visit counts
  day_of_week_pattern?: { [day: string]: number }; // Daily visit counts
  device_type?: string; // Device type for multi-device analysis
}

export interface Route {
  id: number;
  route_name?: string;
  path_coordinates: number[][];
  start_location?: { [key: string]: any };
  end_location?: { [key: string]: any };
  distance?: number;
  average_duration?: number;
  frequency?: number;
  students?: string[];
  consistency_score?: number;
  device_type?: string; // Device type for multi-device analysis
}

export interface AnalysisResults {
  job_id: number;
  analysis_type: string;
  status: string;
  summary_stats?: { [key: string]: any };
  dwell_locations: DwellLocation[];
  routes: Route[];
  reports: string[];
  processing_duration?: number;
  completed_at?: string;
}

// Report Types
export enum ReportType {
  HTML_INTERACTIVE = 'html_interactive',
  PDF_SUMMARY = 'pdf_summary',
  CSV_DATA = 'csv_data',
  JSON_DATA = 'json_data',
  MAP_EXPORT = 'map_export',
}

export interface Report {
  id: number;
  title?: string;
  report_type: string;
  file_path: string;
  file_size?: number;
  summary_stats?: { [key: string]: any };
  is_public: boolean;
  download_count: number;
  generated_at: string;
  expires_at?: string;
}

// UI Types
export interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

export interface NotificationState {
  open: boolean;
  message: string;
  severity: 'error' | 'warning' | 'info' | 'success';
}

// Export scheduled analysis types
export * from './scheduled';